import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Search, Building2, User, ExternalLink, Loader2, Mail, Globe, Users, MapPin, Briefcase, Factory, ChevronDown, ChevronRight, X, Upload, Check, Phone, Calendar, TrendingUp, TrendingDown, Minus, Linkedin, Copy, Link2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// ── Types ──

interface ApolloPersonResult {
  id: string;
  first_name: string;
  last_name_obfuscated: string | null;
  title: string;
  seniority?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  departments?: string[];
  has_email: boolean;
  has_city: boolean;
  has_state: boolean;
  has_country: boolean;
  has_direct_phone: string | null;
  linkedin_url?: string | null;
  organization?: {
    name: string;
    website_url?: string | null;
    logo_url?: string | null;
    industry?: string | null;
    estimated_num_employees?: number | null;
    has_industry: boolean;
    has_phone: boolean;
    has_city: boolean;
    has_state: boolean;
    has_country: boolean;
    has_revenue: boolean;
    has_employee_count: boolean;
  };
}

interface ApolloCompanyResult {
  id: string;
  name: string;
  website_url: string | null;
  linkedin_url: string | null;
  twitter_url?: string | null;
  facebook_url?: string | null;
  primary_domain: string | null;
  industry: string | null;
  industries?: string[];
  keywords?: string[];
  estimated_num_employees: number | null;
  organization_revenue: number | null;
  organization_revenue_printed: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code?: string | null;
  street_address?: string | null;
  short_description: string | null;
  founded_year: number | null;
  phone: string | null;
  logo_url: string | null;
  num_contacts: number | null;
  hubspot_id: string | null;
  hubspot_record_url: string | null;
  source_display_name: string | null;
  sic_codes?: string[];
  naics_codes?: string[];
  organization_headcount_six_month_growth?: number | null;
  organization_headcount_twelve_month_growth?: number | null;
  organization_headcount_twenty_four_month_growth?: number | null;
}

interface ApolloRateLimits {
  minute: { used: number; limit: number; remaining: number };
  hourly: { used: number; limit: number; remaining: number };
  daily: { used: number; limit: number; remaining: number };
}

interface ApolloSearchResponse {
  people?: ApolloPersonResult[];
  accounts?: ApolloCompanyResult[];
  pagination?: {
    page: number;
    per_page: number;
    total_entries: number;
    total_pages: number;
  };
  _rateLimits?: ApolloRateLimits;
}

// ── API ──

async function searchPeople(params: Record<string, unknown>): Promise<ApolloSearchResponse> {
  return api.post<ApolloSearchResponse>('/v4/apollo/people/search', params);
}

async function searchCompanies(params: Record<string, unknown>): Promise<ApolloSearchResponse> {
  return api.post<ApolloSearchResponse>('/v4/apollo/companies/search', params);
}

interface HubSpotCompanyMatch {
  hubspot_company_id: string;
  pg_company_id: string;
  apollo_company_id?: string | null;
  name: string;
  phone?: string | null;
  email?: string | null;
  similarity?: number;
}
interface HubSpotContactMatch {
  hubspot_contact_id: string;
  pg_contact_id: string;
  apollo_contact_id?: string | null;
  full_name: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile_phone?: string | null;
  title?: string | null;
  similarity?: number;
}
interface HubSpotCheckResponse {
  domains: Record<string, HubSpotCompanyMatch>;
  linkedinUrls: Record<string, HubSpotContactMatch>;
  companyNames: Record<string, HubSpotCompanyMatch>;
  personFullNames: Record<string, HubSpotContactMatch>;
}

async function checkHubspot(body: {
  domains?: string[];
  linkedinUrls?: string[];
  companyNames?: string[];
  personFullNames?: Array<{ name: string; companyDomain?: string; companyName?: string }>;
}): Promise<HubSpotCheckResponse> {
  return api.post<HubSpotCheckResponse>('/v4/apollo/check-hubspot', body);
}

async function attachApolloId(body: {
  type: 'contact' | 'company';
  pgId: string;
  apolloId: string;
  hubspotId?: string;
}): Promise<{ pg: boolean; hubspot: boolean | 'skipped'; hubspot_error?: string }> {
  return api.post('/v4/apollo/attach-apollo-id', body);
}

async function searchOrganizations(params: Record<string, unknown>): Promise<ApolloSearchResponse> {
  const result = await api.post<{ organizations: ApolloCompanyResult[]; pagination: ApolloSearchResponse['pagination']; _rateLimits?: ApolloSearchResponse['_rateLimits'] }>('/v4/apollo/organizations/search', params);
  // Normalize to same shape as company search
  return { accounts: result.organizations as unknown as ApolloCompanyResult[], pagination: result.pagination, _rateLimits: result._rateLimits };
}

// ── Persistence ──

const STORAGE_KEYS = {
  locations: 'apollo-selected-locations',
  industries: 'apollo-selected-industries',
  employees: 'apollo-selected-employees',
} as const;

const DEFAULTS = {
  locations: ['Canada'],
  industries: ['Biotechnology', 'Pharmaceuticals'],
  employees: ['11,50', '51,200', '201,1000', '1001,5000', '5001,10000', '10001'],
};

function loadPersisted(key: string, defaults: string[]): string[] {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return defaults;
}

function persist(key: string, values: string[]) {
  localStorage.setItem(key, JSON.stringify(values));
}

// ── Constants ──

const SENIORITY_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'founder', label: 'Founder' },
  { value: 'c_suite', label: 'C-Suite' },
  { value: 'partner', label: 'Partner' },
  { value: 'vp', label: 'VP' },
  { value: 'head', label: 'Head' },
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior', label: 'Senior' },
  { value: 'entry', label: 'Entry' },
  { value: 'intern', label: 'Intern' },
];

const INDUSTRIES = [
  'Biotechnology',
  'Hospital & Health Care',
  'Medical Devices',
  'Pharmaceuticals',
  'Health, Wellness and Fitness',
  'Environmental Services',
  'Research',
  'Food & Beverages',
  'Chemicals',
  // 'Oil & Energy',
  // 'Mining & Metals',
  // 'Utilities',
  'Government Administration',
  // 'Defense & Space',
  // 'Information Technology and Services',
  // 'Computer Software',
  // 'Telecommunications',
  // 'Electrical/Electronic Manufacturing',
  // 'Industrial Automation',
  // 'Mechanical or Industrial Engineering',
  // 'Construction',
  // 'Architecture & Planning',
  // 'Facilities Services',
  // 'Real Estate',
  // 'Education Management',
  'Higher Education',
  // 'Financial Services',
  // 'Banking',
  // 'Insurance',
  'Logistics and Supply Chain',
  'Transportation/Trucking/Railroad',
  'Warehousing',
  'Agriculture',
  'Cannabis',
];

const LOCATIONS = [
  // Canada
  'Canada',
  'Quebec, Canada',
  'Montreal, Quebec, Canada',
  'Quebec City, Quebec, Canada',
  'Ontario, Canada',
  'Toronto, Ontario, Canada',
  'Ottawa, Ontario, Canada',
  'British Columbia, Canada',
  'Vancouver, British Columbia, Canada',
  'Alberta, Canada',
  'Calgary, Alberta, Canada',
  // United States
  'United States',
  'New York, New York, United States',
  'California, United States',
  'Texas, United States',
  'Massachusetts, United States',
  // International
  // 'United Kingdom',
  // 'France',
  // 'Germany',
  // 'Switzerland',
  // 'Australia',
];

const JOB_TITLES = [
  // Facilities / Operations
  'Facilities Manager',
  'Director of Facilities',
  'VP Operations',
  'Operations Manager',
  'Plant Manager',
  'Building Manager',
  'Directeur de production',
  // Quality / Compliance
  'Quality Manager',
  'Quality Director',
  'Compliance Manager',
  'QA Manager',
  'Regulatory Affairs',
  // C-Suite / Leadership
  'CEO',
  'COO',
  'CFO',
  'VP Facilities',
  'General Manager',
  'Maintenance Manager',
];

const EMPLOYEE_RANGES = [
  { value: '1,10', label: '1-10', disabled: true },
  { value: '11,50', label: '11-50' },
  { value: '51,200', label: '51-200' },
  { value: '201,1000', label: '201-1,000' },
  { value: '1001,5000', label: '1,001-5,000' },
  { value: '5001,10000', label: '5,001-10,000' },
  { value: '10001', label: '10,000+' },
];

// ── Collapsible filter section ──

function FilterSection({ title, icon: Icon, children, defaultOpen = true }: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b last:border-b-0">
      <button
        type="button"
        className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-left hover:bg-muted/50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <Icon className="h-4 w-4 text-muted-foreground" />
        {title}
        {open
          ? <ChevronDown className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
          : <ChevronRight className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
        }
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

// ── Detail helpers ──

function DetailRow({ label, value, icon: Icon }: { label: string; value: React.ReactNode; icon?: React.ComponentType<{ className?: string }> }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5">
      {Icon && <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />}
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

function GrowthBadge({ value, label }: { value: number | null | undefined; label: string }) {
  if (value == null) return null;
  const pct = (value * 100).toFixed(1);
  const isPositive = value > 0;
  const isZero = value === 0;
  const Icon = isPositive ? TrendingUp : isZero ? Minus : TrendingDown;
  const color = isPositive ? 'text-green-600' : isZero ? 'text-muted-foreground' : 'text-red-600';
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`h-3.5 w-3.5 ${color}`} />
      <span className={`text-sm font-medium ${color}`}>{isPositive ? '+' : ''}{pct}%</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function RawJsonBlock({ data }: { data: unknown }) {
  const [expanded, setExpanded] = useState(false);
  const json = JSON.stringify(data, null, 2);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(json);
      toast.success('JSON copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Raw JSON
        </button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={handleCopy}>
          <Copy className="h-3 w-3 mr-1" /> Copy
        </Button>
      </div>
      {expanded && (
        <pre className="text-[11px] leading-relaxed font-mono bg-muted/50 border rounded p-2 overflow-auto max-h-[60vh] whitespace-pre">
          {json}
        </pre>
      )}
    </div>
  );
}

function CompanyDetailSheet({ company, open, onOpenChange, pushedToHubspot, onPushToHubspot, pushingId }: {
  company: ApolloCompanyResult | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pushedToHubspot: Record<string, string>;
  onPushToHubspot: (company: ApolloCompanyResult) => void;
  pushingId: string | null;
}) {
  if (!company) return null;
  const location = [company.street_address, company.city, company.state, company.country, company.postal_code].filter(Boolean).join(', ');
  const hasGrowth = company.organization_headcount_six_month_growth != null ||
    company.organization_headcount_twelve_month_growth != null ||
    company.organization_headcount_twenty_four_month_growth != null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-[560px]">
        <SheetHeader>
          <div className="flex items-center gap-3">
            {company.logo_url && <img src={company.logo_url} alt="" className="h-10 w-10 rounded object-contain bg-muted p-1" />}
            <div>
              <SheetTitle className="text-left">{company.name}</SheetTitle>
              {company.primary_domain && <div className="text-xs text-muted-foreground">{company.primary_domain}</div>}
            </div>
          </div>
        </SheetHeader>

        {company.short_description && (
          <p className="text-sm text-muted-foreground mt-3">{company.short_description}</p>
        )}

        <Separator className="my-4" />

        <div className="space-y-1">
          <DetailRow label="Industry" value={company.industry} icon={Factory} />
          {company.industries && company.industries.length > 1 && (
            <DetailRow label="Other Industries" value={company.industries.filter(i => i !== company.industry).join(', ')} />
          )}
          <DetailRow label="Employees" value={company.estimated_num_employees?.toLocaleString()} icon={Users} />
          <DetailRow label="Revenue" value={company.organization_revenue_printed} />
          <DetailRow label="Founded" value={company.founded_year} icon={Calendar} />
          <DetailRow label="Phone" value={company.phone} icon={Phone} />
          <DetailRow label="Location" value={location || null} icon={MapPin} />
        </div>

        {hasGrowth && (
          <>
            <Separator className="my-4" />
            <div className="text-xs font-medium text-muted-foreground mb-2">Headcount Growth</div>
            <div className="space-y-1.5">
              <GrowthBadge value={company.organization_headcount_six_month_growth} label="6 months" />
              <GrowthBadge value={company.organization_headcount_twelve_month_growth} label="12 months" />
              <GrowthBadge value={company.organization_headcount_twenty_four_month_growth} label="24 months" />
            </div>
          </>
        )}

        {company.keywords && company.keywords.length > 0 && (
          <>
            <Separator className="my-4" />
            <div className="text-xs font-medium text-muted-foreground mb-2">Keywords</div>
            <div className="flex flex-wrap gap-1.5">
              {company.keywords.map((kw) => (
                <Badge key={kw} variant="secondary" className="text-xs">{kw}</Badge>
              ))}
            </div>
          </>
        )}

        <Separator className="my-4" />
        <div className="text-xs font-medium text-muted-foreground mb-2">Links</div>
        <div className="flex flex-wrap gap-2">
          {company.website_url && (
            <a href={company.website_url} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-muted"><Globe className="h-3 w-3 mr-1" /> Website</Badge>
            </a>
          )}
          {company.linkedin_url && (
            <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-muted"><Linkedin className="h-3 w-3 mr-1" /> LinkedIn</Badge>
            </a>
          )}
          {company.twitter_url && (
            <a href={company.twitter_url} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-muted"><ExternalLink className="h-3 w-3 mr-1" /> Twitter</Badge>
            </a>
          )}
          {company.facebook_url && (
            <a href={company.facebook_url} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-muted"><ExternalLink className="h-3 w-3 mr-1" /> Facebook</Badge>
            </a>
          )}
        </div>

        {(company.sic_codes?.length || company.naics_codes?.length) ? (
          <>
            <Separator className="my-4" />
            <div className="text-xs font-medium text-muted-foreground mb-2">Classification Codes</div>
            {company.sic_codes?.length ? <DetailRow label="SIC" value={company.sic_codes.join(', ')} /> : null}
            {company.naics_codes?.length ? <DetailRow label="NAICS" value={company.naics_codes.join(', ')} /> : null}
          </>
        ) : null}

        <Separator className="my-4" />
        <div className="text-xs font-medium text-muted-foreground mb-2">HubSpot</div>
        {company.hubspot_id || pushedToHubspot[company.id] ? (
          <a
            href={company.hubspot_record_url || `https://app.hubspot.com/contacts/22484617/company/${company.hubspot_id || pushedToHubspot[company.id]}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Badge variant="outline" className="text-sm text-green-600 border-green-300 cursor-pointer hover:bg-green-50 py-1.5 px-3">
              <Check className="h-4 w-4 mr-1.5" /> View in HubSpot
            </Badge>
          </a>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={pushingId === company.id}
            onClick={() => onPushToHubspot(company)}
          >
            {pushingId === company.id ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Upload className="h-4 w-4 mr-1" />
            )}
            Push to HubSpot
          </Button>
        )}

        <Separator className="my-4" />
        <div className="text-xs text-muted-foreground mb-3">Apollo ID: <span className="font-mono">{company.id}</span></div>

        <Separator className="my-4" />
        <RawJsonBlock data={company} />
      </SheetContent>
    </Sheet>
  );
}

function PersonDetailSheet({ person, open, onOpenChange }: { person: ApolloPersonResult | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  if (!person) return null;
  const location = [person.city, person.state, person.country].filter(Boolean).join(', ');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-[560px]">
        <SheetHeader>
          <SheetTitle className="text-left">{person.first_name} {person.last_name_obfuscated || ''}</SheetTitle>
          {person.title && <div className="text-sm text-muted-foreground">{person.title}</div>}
        </SheetHeader>

        <Separator className="my-4" />

        <div className="space-y-1">
          {person.seniority && <DetailRow label="Seniority" value={person.seniority} icon={Briefcase} />}
          {person.departments && person.departments.length > 0 && (
            <DetailRow label="Department" value={person.departments.join(', ')} />
          )}
          <DetailRow label="Location" value={location || null} icon={MapPin} />
          <DetailRow label="Email" value={person.has_email ? 'Available (reveal to see)' : 'Not available'} icon={Mail} />
          <DetailRow label="Direct Phone" value={person.has_direct_phone || 'Not available'} icon={Phone} />
          {person.linkedin_url && (
            <DetailRow label="LinkedIn" value={
              <a href={person.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                View Profile <ExternalLink className="h-3 w-3" />
              </a>
            } icon={Linkedin} />
          )}
        </div>

        {person.organization && (
          <>
            <Separator className="my-4" />
            <div className="text-xs font-medium text-muted-foreground mb-2">Company</div>
            <div className="space-y-1">
              <DetailRow label="Company" value={person.organization.name} icon={Building2} />
              {person.organization.industry && <DetailRow label="Industry" value={person.organization.industry} icon={Factory} />}
              {person.organization.estimated_num_employees && (
                <DetailRow label="Employees" value={person.organization.estimated_num_employees.toLocaleString()} icon={Users} />
              )}
              {person.organization.website_url && (
                <DetailRow label="Website" value={
                  <a href={person.organization.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                    {person.organization.website_url} <ExternalLink className="h-3 w-3" />
                  </a>
                } icon={Globe} />
              )}
            </div>
          </>
        )}

        <Separator className="my-4" />
        <div className="text-xs text-muted-foreground mb-3">Apollo ID: <span className="font-mono">{person.id}</span></div>

        <Separator className="my-4" />
        <RawJsonBlock data={person} />
      </SheetContent>
    </Sheet>
  );
}

// ── Page ──

export function ApolloPage() {
  const [tab, setTab] = useState<'people' | 'companies'>('people');
  const [prospecting, setProspecting] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedTitles, setSelectedTitles] = useState<string[]>([]);
  const [customTitle, setCustomTitle] = useState('');
  const [selectedSeniorities, setSelectedSeniorities] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>(() => loadPersisted(STORAGE_KEYS.locations, DEFAULTS.locations));
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(() => loadPersisted(STORAGE_KEYS.industries, DEFAULTS.industries));
  const [selectedEmployeeRanges, setSelectedEmployeeRanges] = useState<string[]>(() => loadPersisted(STORAGE_KEYS.employees, DEFAULTS.employees));
  const [page, setPage] = useState(1);
  const [rateLimits, setRateLimits] = useState<ApolloRateLimits | null>(null);

  useEffect(() => { persist(STORAGE_KEYS.locations, selectedLocations); }, [selectedLocations]);
  useEffect(() => { persist(STORAGE_KEYS.industries, selectedIndustries); }, [selectedIndustries]);
  useEffect(() => { persist(STORAGE_KEYS.employees, selectedEmployeeRanges); }, [selectedEmployeeRanges]);

  const peopleMutation = useMutation({
    mutationFn: searchPeople,
    onSuccess: (data) => { if (data._rateLimits) setRateLimits(data._rateLimits); },
    onError: (err) => toast.error('People search failed', { description: err.message }),
  });

  const companiesMutation = useMutation({
    mutationFn: searchCompanies,
    onSuccess: (data) => { if (data._rateLimits) setRateLimits(data._rateLimits); },
    onError: (err) => toast.error('Company search failed', { description: err.message }),
  });

  const orgMutation = useMutation({
    mutationFn: searchOrganizations,
    onSuccess: (data) => { if (data._rateLimits) setRateLimits(data._rateLimits); },
    onError: (err) => toast.error('Organization search failed', { description: err.message }),
  });

  const activeCompanyMutation = prospecting ? orgMutation : companiesMutation;

  // Track which companies have been pushed to HubSpot
  const [selectedPerson, setSelectedPerson] = useState<ApolloPersonResult | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<ApolloCompanyResult | null>(null);
  const [pushedToHubspot, setPushedToHubspot] = useState<Record<string, string>>({});
  const [pushingId, setPushingId] = useState<string | null>(null);

  // Results cross-checked against our PG (HubSpot CDC) — no Apollo credits used.
  // Keyed by lowercase domain (companies), lowercase linkedin url (people), and fuzzy-matched names.
  const [hubspotByDomain, setHubspotByDomain] = useState<HubSpotCheckResponse['domains']>({});
  const [hubspotByLinkedin, setHubspotByLinkedin] = useState<HubSpotCheckResponse['linkedinUrls']>({});
  const [hubspotByCompanyName, setHubspotByCompanyName] = useState<HubSpotCheckResponse['companyNames']>({});
  const [hubspotByPersonName, setHubspotByPersonName] = useState<HubSpotCheckResponse['personFullNames']>({});

  // Apollo IDs the user linked manually this session (keyed by Apollo id). Hides the Link button + keeps badge.
  const [linkedApolloIds, setLinkedApolloIds] = useState<Record<string, true>>({});
  const [linkingApolloId, setLinkingApolloId] = useState<string | null>(null);

  async function handleManualLink(
    type: 'contact' | 'company',
    apolloId: string,
    match: HubSpotCompanyMatch | HubSpotContactMatch,
  ) {
    setLinkingApolloId(apolloId);
    try {
      const pgId = (match as HubSpotCompanyMatch).pg_company_id ?? (match as HubSpotContactMatch).pg_contact_id;
      const hubspotId = (match as HubSpotCompanyMatch).hubspot_company_id ?? (match as HubSpotContactMatch).hubspot_contact_id;
      const result = await attachApolloId({ type, pgId, apolloId, hubspotId });
      setLinkedApolloIds((prev) => ({ ...prev, [apolloId]: true }));
      if (result.hubspot === true) toast.success('Linked to HubSpot + PG');
      else if (result.hubspot === false) toast.warning('Linked to PG (HubSpot sync failed)', { description: result.hubspot_error });
      else toast.success('Linked to PG');
    } catch (e: any) {
      toast.error('Link failed', { description: e.message });
    } finally {
      setLinkingApolloId(null);
    }
  }

  async function pushToHubspot(company: ApolloCompanyResult) {
    setPushingId(company.id);
    try {
      const result = await api.post<{ hubspot_id: string; name: string }>('/v4/apollo/push-to-hubspot', { company });
      setPushedToHubspot((prev) => ({ ...prev, [company.id]: result.hubspot_id }));
      toast.success(`${company.name} pushed to HubSpot`);
    } catch (err: any) {
      const data = err?.data;
      if (data?.hubspot_id) {
        // Already exists
        setPushedToHubspot((prev) => ({ ...prev, [company.id]: data.hubspot_id }));
        toast.info(`${company.name} already in HubSpot`);
      } else {
        toast.error('Push failed', { description: data?.error || err.message });
      }
    } finally {
      setPushingId(null);
    }
  }

  // Load initial usage on mount
  useEffect(() => {
    api.get<{ rateLimits: ApolloRateLimits }>('/v4/apollo/usage')
      .then((res) => { if (res.rateLimits) setRateLimits(res.rateLimits); })
      .catch(() => {}); // silent
  }, []);

  const isLoading = peopleMutation.isPending || activeCompanyMutation.isPending;

  const allTitles = [...selectedTitles, ...(customTitle.trim() ? [customTitle.trim()] : [])];

  const activeFilterCount = [
    query, ...allTitles,
    ...selectedSeniorities, ...selectedLocations, ...selectedIndustries, ...selectedEmployeeRanges,
  ].filter(Boolean).length;

  function toggleArrayItem(arr: string[], item: string): string[] {
    return arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
  }

  function clearFilters() {
    setQuery('');
    setSelectedTitles([]);
    setCustomTitle('');
    setSelectedSeniorities([]);
    setSelectedLocations([]);
    setSelectedIndustries([]);
    setSelectedEmployeeRanges([]);
  }

  function buildBody(pageNum: number): Record<string, unknown> {
    const body: Record<string, unknown> = { page: pageNum, per_page: 100 };
    if (query.trim()) body.q_organization_name = query.trim();
    if (selectedEmployeeRanges.length) body.organization_num_employees_ranges = selectedEmployeeRanges;

    if (tab === 'people') {
      if (allTitles.length) body.person_titles = allTitles;
      if (selectedSeniorities.length) body.person_seniorities = selectedSeniorities;
      if (selectedIndustries.length) body.q_organization_keyword_tags = selectedIndustries;
      if (selectedLocations.length) body.person_locations = selectedLocations;
    } else {
      if (selectedIndustries.length) body.q_organization_keyword_tags = selectedIndustries;
      if (selectedLocations.length) body.organization_locations = selectedLocations;
    }
    return body;
  }

  function handleSearch(e?: React.FormEvent) {
    e?.preventDefault();
    if (activeFilterCount === 0) {
      toast.error('Enter at least one search criteria');
      return;
    }
    setPage(1);
    const body = buildBody(1);
    if (tab === 'people') peopleMutation.mutate(body);
    else activeCompanyMutation.mutate(body);
  }

  function handlePageChange(newPage: number) {
    setPage(newPage);
    const body = buildBody(newPage);
    if (tab === 'people') peopleMutation.mutate(body);
    else activeCompanyMutation.mutate(body);
  }

  const pagination = tab === 'people'
    ? peopleMutation.data?.pagination
    : activeCompanyMutation.data?.pagination;

  const people = peopleMutation.data?.people || [];
  const companies = activeCompanyMutation.data?.accounts || [];

  // Auto-check HubSpot presence whenever results arrive (read-only PG lookup, zero Apollo credits).
  useEffect(() => {
    const domains = [...new Set(companies.map((c) => c.primary_domain).filter(Boolean) as string[])];
    const companyNames = [...new Set(companies.map((c) => c.name).filter(Boolean) as string[])];
    const linkedinUrls = [...new Set(people.map((p) => p.linkedin_url).filter(Boolean) as string[])];
    const personFullNames = people
      .filter((p) => p.first_name)
      .map((p) => {
        // Apollo obfuscates with asterisks — backend strips them before trigram.
        const rawName = `${p.first_name} ${p.last_name_obfuscated || ''}`.trim();
        const domain = p.organization?.website_url
          ? p.organization.website_url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]
          : undefined;
        return {
          name: rawName,
          companyDomain: domain,
          companyName: p.organization?.name,
        };
      })
      .filter((p) => p.name.length >= 3);
    if (!domains.length && !linkedinUrls.length && !companyNames.length && !personFullNames.length) return;
    checkHubspot({ domains, linkedinUrls, companyNames, personFullNames })
      .then((res) => {
        if (res.domains) setHubspotByDomain((prev) => ({ ...prev, ...res.domains }));
        if (res.linkedinUrls) setHubspotByLinkedin((prev) => ({ ...prev, ...res.linkedinUrls }));
        if (res.companyNames) setHubspotByCompanyName((prev) => ({ ...prev, ...res.companyNames }));
        if (res.personFullNames) setHubspotByPersonName((prev) => ({ ...prev, ...res.personFullNames }));

        // Auto-attach Apollo ID on discovery. For each match that has HubSpot linkage but no
        // apollo_* id stored in PG, persist the Apollo id (PG + best-effort HubSpot custom property).
        const attachments: Array<Parameters<typeof attachApolloId>[0]> = [];

        for (const c of companies) {
          if (!c.primary_domain) continue;
          const m = res.domains?.[c.primary_domain.toLowerCase()];
          if (m && !m.apollo_company_id) {
            attachments.push({ type: 'company', pgId: m.pg_company_id, apolloId: c.id, hubspotId: m.hubspot_company_id });
          }
        }
        for (const p of people) {
          const liKey = p.linkedin_url?.toLowerCase();
          const m = liKey ? res.linkedinUrls?.[liKey] : undefined;
          if (m && !m.apollo_contact_id) {
            attachments.push({ type: 'contact', pgId: m.pg_contact_id, apolloId: p.id, hubspotId: m.hubspot_contact_id });
          }
        }

        // Fire and forget — no toasts, no state updates. Next search will see updated apollo_*_id.
        attachments.forEach((body) => { attachApolloId(body).catch(() => {}); });
      })
      .catch(() => { /* silent — optional badge */ });
  }, [peopleMutation.data, activeCompanyMutation.data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-[calc(100vh-50px)]">
      {/* ── Left Sidebar: Filters ── */}
      <div className="w-[280px] min-w-[280px] border-r bg-background overflow-y-auto">
        {/* Sidebar header */}
        <div className="px-3 py-3 border-b flex items-center justify-between gap-2">
          <div className="flex gap-1 min-w-0">
            <Button
              size="sm"
              variant={tab === 'people' ? 'default' : 'ghost'}
              className="h-7 text-xs px-2"
              onClick={() => setTab('people')}
            >
              <User className="h-3.5 w-3.5 mr-1" /> People
            </Button>
            <Button
              size="sm"
              variant={tab === 'companies' ? 'default' : 'ghost'}
              className="h-7 text-xs px-2"
              onClick={() => setTab('companies')}
            >
              <Building2 className="h-3.5 w-3.5 mr-1" /> Companies
            </Button>
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 shrink-0 text-muted-foreground"
              onClick={clearFilters}
              title="Clear filters"
              aria-label="Clear filters"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Prospecting toggle (companies only) */}
        {tab === 'companies' && (
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Prospection</span>
            <Switch checked={prospecting} onCheckedChange={setProspecting} />
          </div>
        )}

        {/* Search button */}
        <div className="px-3 py-2 border-b">
          <Button className="w-full" size="sm" onClick={() => handleSearch()} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
            Search
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">{activeFilterCount}</Badge>
            )}
          </Button>
        </div>

        {/* Filter sections */}
        <div>
          {/* Company name */}
          <FilterSection title="Company" icon={Building2}>
            <Input
              placeholder="Company name..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </FilterSection>

          {/* Job title (people only) */}
          {tab === 'people' && (
            <FilterSection title="Job Title" icon={Briefcase}>
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
                {JOB_TITLES.map((title) => (
                  <label key={title} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                    <Checkbox
                      checked={selectedTitles.includes(title)}
                      onCheckedChange={() => setSelectedTitles(toggleArrayItem(selectedTitles, title))}
                    />
                    <span className="truncate">{title}</span>
                  </label>
                ))}
              </div>
              <Input
                placeholder="Custom title..."
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                className="h-8 text-sm mt-2"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </FilterSection>
          )}

          {/* Seniority (people only) */}
          {tab === 'people' && (
            <FilterSection title="Seniority" icon={User} defaultOpen={false}>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {SENIORITY_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                    <Checkbox
                      checked={selectedSeniorities.includes(opt.value)}
                      onCheckedChange={() => setSelectedSeniorities(toggleArrayItem(selectedSeniorities, opt.value))}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </FilterSection>
          )}

          {/* Location */}
          <FilterSection title="Location" icon={MapPin}>
            <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
              {LOCATIONS.map((loc) => (
                <label key={loc} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                  <Checkbox
                    checked={selectedLocations.includes(loc)}
                    onCheckedChange={() => setSelectedLocations(toggleArrayItem(selectedLocations, loc))}
                  />
                  <span className="truncate">{loc}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Industry */}
          <FilterSection title="Industry" icon={Factory} defaultOpen={false}>
            <div className="space-y-1.5 max-h-[240px] overflow-y-auto">
              {INDUSTRIES.map((ind) => (
                <label key={ind} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                  <Checkbox
                    checked={selectedIndustries.includes(ind)}
                    onCheckedChange={() => setSelectedIndustries(toggleArrayItem(selectedIndustries, ind))}
                  />
                  <span className="truncate">{ind}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Employees */}
          <FilterSection title="Employees" icon={Users} defaultOpen={false}>
            <div className="space-y-1.5">
              {EMPLOYEE_RANGES.map((range) => (
                <label key={range.value} className={`flex items-center gap-2 text-sm rounded px-1 py-0.5 ${range.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}>
                  <Checkbox
                    checked={selectedEmployeeRanges.includes(range.value)}
                    onCheckedChange={() => !range.disabled && setSelectedEmployeeRanges(toggleArrayItem(selectedEmployeeRanges, range.value))}
                    disabled={range.disabled}
                  />
                  {range.label}
                </label>
              ))}
            </div>
          </FilterSection>
        </div>
      </div>

      {/* ── Right: Results ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Results header */}
        <div className="px-4 py-3 border-b flex items-center justify-between bg-background">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold">Apollo Search</h1>
            {pagination && (
              <span className="text-sm text-muted-foreground">
                {pagination.total_entries.toLocaleString()} results
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Daily credit usage */}
            {rateLimits && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">Requests:</span>
                <span className={rateLimits.daily.remaining < 1000 ? 'text-orange-600 font-semibold' : 'font-medium'}>
                  {rateLimits.daily.used.toLocaleString()}
                </span>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground">{rateLimits.daily.limit.toLocaleString()}</span>
              </div>
            )}
            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {page} / {pagination.total_pages}
                </span>
                <Button variant="outline" size="sm" disabled={page <= 1 || isLoading} onClick={() => handlePageChange(page - 1)}>
                  Prev
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pagination.total_pages || isLoading} onClick={() => handlePageChange(page + 1)}>
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Results table */}
        <div className="flex-1 overflow-auto">
          {/* People table */}
          {tab === 'people' && people.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky top-0 bg-background w-[48px]"></TableHead>
                  <TableHead className="sticky top-0 bg-background">Name</TableHead>
                  <TableHead className="sticky top-0 bg-background">Title</TableHead>
                  <TableHead className="sticky top-0 bg-background">Company</TableHead>
                  <TableHead className="sticky top-0 bg-background text-center">Email</TableHead>
                  <TableHead className="sticky top-0 bg-background text-center">Phone</TableHead>
                  <TableHead className="sticky top-0 bg-background text-center">Location</TableHead>
                  <TableHead className="sticky top-0 bg-background w-[120px]">HubSpot</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {people.map((p) => {
                  const liKey = p.linkedin_url?.toLowerCase();
                  const hsByLi = liKey ? hubspotByLinkedin[liKey] : undefined;
                  // Fuzzy key must mirror the backend: strip asterisks from name, prefer domain then company name as scope.
                  const rawName = `${p.first_name} ${p.last_name_obfuscated || ''}`.trim();
                  const strippedName = rawName.replace(/\*+/g, '').replace(/\s+/g, ' ').trim();
                  const personDomain = p.organization?.website_url
                    ? p.organization.website_url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0].toLowerCase()
                    : undefined;
                  const scope = personDomain || p.organization?.name || '';
                  const fuzzyKey = scope ? `${strippedName}|${scope}` : strippedName;
                  const hsByName = strippedName.length >= 3 ? hubspotByPersonName[fuzzyKey] : undefined;
                  const hsPerson = hsByLi || hsByName;
                  const rowTint = hsPerson ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-muted/50';
                  return (
                  <TableRow key={p.id} className={`cursor-pointer ${rowTint}`} onClick={() => setSelectedPerson(p)}>
                    <TableCell>
                      {p.organization?.logo_url ? (
                        <img
                          src={p.organization.logo_url}
                          alt=""
                          className="h-8 w-8 rounded object-contain bg-muted/40 p-0.5"
                          loading="lazy"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded bg-muted/40 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap">
                      {hsPerson?.first_name && hsPerson?.last_name ? (
                        <span title="Name from HubSpot (PostgreSQL)">{hsPerson.first_name} {hsPerson.last_name}</span>
                      ) : (
                        <>{p.first_name} {p.last_name_obfuscated || ''}</>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{p.title || hsPerson?.title || ''}</TableCell>
                    <TableCell className="text-sm font-medium">
                      {p.organization?.name || '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      {hsPerson?.email ? (
                        <a href={`mailto:${hsPerson.email}`} onClick={(e) => e.stopPropagation()} className="text-xs text-blue-600 hover:underline" title="From HubSpot (PostgreSQL)">
                          {hsPerson.email}
                        </a>
                      ) : p.has_email ? (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          <Mail className="h-3 w-3 mr-1" /> Available
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {hsPerson?.mobile_phone || hsPerson?.phone ? (
                        <a href={`tel:${hsPerson.mobile_phone || hsPerson.phone}`} onClick={(e) => e.stopPropagation()} className="text-xs text-blue-600 hover:underline" title="From HubSpot (PostgreSQL)">
                          {hsPerson.mobile_phone || hsPerson.phone}
                        </a>
                      ) : p.has_direct_phone === 'Yes' ? (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">Available</Badge>
                      ) : p.has_direct_phone?.startsWith('Maybe') ? (
                        <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300">Maybe</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {p.has_country ? (
                        <Badge variant="outline" className="text-xs">
                          <MapPin className="h-3 w-3 mr-1" />
                          {[p.has_city && 'City', p.has_state && 'State', 'Country'].filter(Boolean).join(', ')}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {hsPerson ? (() => {
                        const userLinked = linkedApolloIds[p.id];
                        const fuzzyMatch = !hsByLi && hsByName ? hsByName : undefined;
                        const isConfirmed = Boolean(hsByLi) || userLinked;
                        const canLink = fuzzyMatch && !fuzzyMatch.apollo_contact_id && !userLinked;
                        return (
                          <div className="flex items-center gap-1">
                            <a
                              href={`https://app.hubspot.com/contacts/22484617/contact/${hsPerson.hubspot_contact_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={!isConfirmed && fuzzyMatch ? `Fuzzy match (${Math.round((fuzzyMatch.similarity || 0) * 100)}%): ${fuzzyMatch.full_name}` : 'LinkedIn match'}
                            >
                              <Badge variant="outline" className={`text-xs cursor-pointer ${isConfirmed ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-amber-600 border-amber-300 hover:bg-amber-50'}`}>
                                <Check className="h-3 w-3 mr-1" /> {isConfirmed ? 'In HubSpot' : 'Likely'}
                              </Badge>
                            </a>
                            {canLink && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                disabled={linkingApolloId === p.id}
                                onClick={() => handleManualLink('contact', p.id, fuzzyMatch!)}
                                title="Link this Apollo person to the PG row + HubSpot"
                                aria-label="Link Apollo ID"
                              >
                                {linkingApolloId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                              </Button>
                            )}
                          </div>
                        );
                      })() : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          )}

          {/* Companies table */}
          {tab === 'companies' && companies.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky top-0 bg-background w-[48px]"></TableHead>
                  <TableHead className="sticky top-0 bg-background">Company</TableHead>
                  <TableHead className="sticky top-0 bg-background">Industry</TableHead>
                  <TableHead className="sticky top-0 bg-background">Employees</TableHead>
                  <TableHead className="sticky top-0 bg-background">Revenue</TableHead>
                  <TableHead className="sticky top-0 bg-background">Location</TableHead>
                  <TableHead className="sticky top-0 bg-background">Contacts</TableHead>
                  <TableHead className="sticky top-0 bg-background w-[80px]">Links</TableHead>
                  <TableHead className="sticky top-0 bg-background w-[120px]">HubSpot</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((c) => {
                  const pgByDomain = c.primary_domain ? hubspotByDomain[c.primary_domain.toLowerCase()] : undefined;
                  const pgByName = c.name ? hubspotByCompanyName[c.name] : undefined;
                  const hasHsMatch = Boolean(c.hubspot_id || pushedToHubspot[c.id] || pgByDomain || pgByName);
                  const rowTint = hasHsMatch ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-muted/50';
                  return (
                  <TableRow key={c.id} className={`cursor-pointer ${rowTint}`} onClick={() => setSelectedCompany(c)}>
                    <TableCell>
                      {c.logo_url ? (
                        <img
                          src={c.logo_url}
                          alt=""
                          className="h-8 w-8 rounded object-contain bg-muted/40 p-0.5"
                          loading="lazy"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded bg-muted/40 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">{c.name}</span>
                        {c.short_description && (
                          <span className="text-xs text-muted-foreground line-clamp-2 max-w-[300px]">{c.short_description}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{c.industry || '—'}</TableCell>
                    <TableCell>
                      {c.estimated_num_employees
                        ? <span className="flex items-center gap-1 text-sm"><Users className="h-3 w-3" /> {c.estimated_num_employees.toLocaleString()}</span>
                        : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.organization_revenue_printed || '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {[c.city, c.state, c.country].filter(Boolean).join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-center">
                      {c.num_contacts || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {c.website_url && (
                          <a href={c.website_url} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800" title="Website">
                            <Globe className="h-4 w-4" />
                          </a>
                        )}
                        {c.linkedin_url && (
                          <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800" title="LinkedIn">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const userLinked = linkedApolloIds[c.id];
                        const exactId = c.hubspot_id || pushedToHubspot[c.id] || pgByDomain?.hubspot_company_id;
                        const fuzzyId = !exactId ? pgByName?.hubspot_company_id : undefined;
                        const hsId = exactId || fuzzyId;
                        if (!hsId) return null;
                        const fuzzyMatch = fuzzyId ? pgByName : undefined;
                        // Once the user has explicitly linked, treat the row as an exact match.
                        const isConfirmed = Boolean(exactId) || userLinked;
                        const canLink = fuzzyMatch && !fuzzyMatch.apollo_company_id && !userLinked;
                        return (
                          <div className="flex items-center gap-1">
                            <a
                              href={c.hubspot_record_url || `https://app.hubspot.com/contacts/22484617/company/${hsId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={!isConfirmed && fuzzyMatch ? `Fuzzy match (${Math.round((fuzzyMatch.similarity || 0) * 100)}%): ${fuzzyMatch.name}` : 'Exact match'}
                            >
                              <Badge variant="outline" className={`text-xs cursor-pointer ${isConfirmed ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-amber-600 border-amber-300 hover:bg-amber-50'}`}>
                                <Check className="h-3 w-3 mr-1" /> {isConfirmed ? 'In HubSpot' : 'Likely'}
                              </Badge>
                            </a>
                            {canLink && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                disabled={linkingApolloId === c.id}
                                onClick={() => handleManualLink('company', c.id, fuzzyMatch!)}
                                title="Link this Apollo company to the PG row + HubSpot"
                                aria-label="Link Apollo ID"
                              >
                                {linkingApolloId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          )}

          {/* Empty states */}
          {tab === 'people' && peopleMutation.isSuccess && people.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <User className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg">No people found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          )}

          {tab === 'companies' && activeCompanyMutation.isSuccess && companies.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Building2 className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg">No companies found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          )}

          {/* Initial state */}
          {((tab === 'people' && !peopleMutation.isSuccess && !peopleMutation.isPending) ||
            (tab === 'companies' && !activeCompanyMutation.isSuccess && !activeCompanyMutation.isPending)) && (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Search className="h-16 w-16 mb-4 opacity-20" />
              <p className="text-lg">Search Apollo.io</p>
              <p className="text-sm">Use the filters on the left, then click Search</p>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Loader2 className="h-10 w-10 animate-spin mb-4 opacity-40" />
              <p className="text-sm">Searching...</p>
            </div>
          )}
        </div>
      </div>

      <PersonDetailSheet person={selectedPerson} open={!!selectedPerson} onOpenChange={(v) => !v && setSelectedPerson(null)} />
      <CompanyDetailSheet company={selectedCompany} open={!!selectedCompany} onOpenChange={(v) => !v && setSelectedCompany(null)} pushedToHubspot={pushedToHubspot} onPushToHubspot={pushToHubspot} pushingId={pushingId} />
    </div>
  );
}
